import { Component, Input, Output, OnChanges, SimpleChanges, SimpleChange, EventEmitter, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ComponentBase, Changeable } from 'common';
import { UserDto, UserClass, AccountType, GroupDto, getAccountTypeLabel, GetGroupsContainingUserResultDto } from 'UvExplorerServer.Dtos.TS/Auth';
import { UsersService } from '../../services/users.service';
import { MessageService } from '../../../base';
import { HashSet, ObjUtils } from '../../../DiscoveryEngine.TS/Utils';


@Component({
    selector: 'user',
	templateUrl: './user.component.html',
	styleUrls: ['./user.component.scss']
})
export class UserComponent extends ComponentBase implements OnInit, OnChanges, Changeable {

	@Input()
	public target: UserDto;

	@Input()
	public create: boolean;

	public form: UntypedFormGroup;

	public username: UntypedFormControl;
	public firstName: UntypedFormControl;
	public lastName: UntypedFormControl;
	public email: UntypedFormControl;
	public userClass: UntypedFormControl;
	public active: UntypedFormControl;
	public password: UntypedFormControl;
	public confirmPassword: UntypedFormControl;

	public entries: any[];
	public excludedGroupGuids: string[];
	public groupSelectorVisible: boolean;

	private userGroups: HashSet<GroupDto>;
	private originalUserGroups: HashSet<GroupDto>;

	constructor(private fb: UntypedFormBuilder,
		private usersService: UsersService,
		private messageService: MessageService) {

		super();

		this.entries = [];
		this.excludedGroupGuids = [];
		this.groupSelectorVisible = false;

		this.userGroups = new HashSet<GroupDto>();
		this.originalUserGroups = new HashSet<GroupDto>();

		this.buildForm();
	}

	public ngOnInit(): void {
		this.initUserGroups();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.excludedGroupGuids = [];
		this.groupSelectorVisible = false;
		this.initUserGroups();
		this.buildForm();
	}

	private initUserGroups(): void {
		this.userGroups.Clear();
		this.originalUserGroups.Clear();
		this.entries = [];

		if (!this.create && !!this.target) {
			this.usersService.getGroupsContainingUser(this.target.Guid).subscribe(
				(result: GetGroupsContainingUserResultDto) => {
					result.Groups.ForEach((group: GroupDto) => {
						this.userGroups.Add(group);
						this.originalUserGroups.Add(group);
					});
					this.rebuildEntries();
				},
				(error) => {
					this.messageService.displayErrorMessage("Could not retrieve user's groups");
				});
		}
	}

	public get hasGroupEntries(): boolean {
		return (!!this.entries && this.entries.length > 0);
	}

	public getPassword(): string {
		return this.password.value;
	}

	public getIsActiveNativeAdmin(): boolean {
		return (this.active.value && (this.userClass.value === "Administrator") &&
				(this.target.AccountType == AccountType.Native));
	}

	private buildForm(): void {
		let username = "";
		let firstName = "";
		let lastName = "";
		let email = "";
		let userClass = "Regular";
		let active = false;
		let password = "";
		let confirmPassword = "";

		if (this.target) {
			username = this.target.Username;
			firstName = this.target.FirstName;
			lastName = this.target.LastName;
			email = this.target.Email;
			userClass = UserClass[this.target.UserClass];
			active = this.target.IsActive;
			password = "";
			confirmPassword = "";
		}

		this.username = new UntypedFormControl(username, Validators.required);
		this.firstName = new UntypedFormControl(firstName, Validators.required);
		this.lastName = new UntypedFormControl(lastName, Validators.required);
		this.email = new UntypedFormControl(email, Validators.required);
		this.userClass = new UntypedFormControl(userClass);
		this.active = new UntypedFormControl(active);
		this.password = new UntypedFormControl(password);
		this.confirmPassword = new UntypedFormControl(confirmPassword);

		this.enableDisableControls();

		this.form = this.fb.group({
				username: this.username,
				firstName: this.firstName,
				lastName: this.lastName,
				email: this.email,
				userClass: this.userClass,
				active: this.active,
				password: this.password,
				confirmPassword: this.confirmPassword
			},
			{
				validator: Validators.compose([
					this.validPassword(this.password),
					this.passwordsEqual(this.password, this.confirmPassword)
				])
			});
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.password, this.enablePassword);
		this.enableDisableFormControl(this.confirmPassword, this.enablePassword);
	}

	public get enablePassword(): boolean {
		if (this.target) {
			return (this.target.AccountType === AccountType.Native);
		}
		else {
			return false;
		}
	}

	private validPassword(password: UntypedFormControl): ValidatorFn {
		return () => {
			if (this.create && this.enablePassword) {
				if (!password.value) {
					return {
						validPassword: { valid: false }
					};
				}
				else {
					return null;
				}
			}
			else {
				return null;
			}
		}
	}

	private passwordsEqual(password: UntypedFormControl, confirmPassword: UntypedFormControl): ValidatorFn {
		return () => {
			if (password.value !== confirmPassword.value) {
				return {
					passwordsEqual: { valid: false }
				};
			}
			else {
				return null;
			}
		}
	}

	public get enableApply(): boolean {
		return (this.tempChanges && this.tempChangesValid);
	}

	public get tempChanges(): boolean {
		return this.form.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.form.valid;
	}

	public get invalidTempChanges(): string[] {
		let result: string[] = [];
		if (!this.form.valid) {
			result.push("User");
		}
		return result;
	}

	public applyTempChanges(): void {
		if (this.target) {
			this.getChanges(this.target);
			this.buildForm();
		}
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	private getChanges(user: UserDto): void {
		user.Username = this.username.value;
		user.FirstName = this.firstName.value;
		user.LastName = this.lastName.value;
		user.Email = this.email.value;
		user.UserClass = UserClass[<string>this.userClass.value];
		user.IsActive = this.active.value;
		// Don't get password, because it is processed out-of-band
	}

	private rebuildEntries(): void {
		this.entries = [];

		this.userGroups.ForEach((group: GroupDto) => {
			this.addEntry(group);
		});
	}

	private addEntry(group: GroupDto): void {
		if (!this.entries.some((entry) => entry.tag.Guid === group.Guid)) {
			let entry = {
				groupname: group.Name,
				class: UserClass[group.UserClass],
				type: getAccountTypeLabel(group.AccountType),
				active: (group.IsActive ? 'Yes' : 'No'),
				tag: group,
				isNative: (group.AccountType === AccountType.Native),
			};

			this.entries.push(entry);
		}
	}

	public addGroups(): void {
		this.rebuildExcludedGroupGuids();
		this.groupSelectorVisible = true;
	}

	private rebuildExcludedGroupGuids(): void {
		let result = this.groupsToGuids(this.userGroups);

		this.usersService.groupTree.Groups.ForEach((group: GroupDto) => {
			if (!group.HasName || group.AccountType !== AccountType.Native) {
				result.Add(group.Guid);
			}
		});

		this.excludedGroupGuids = [];
		result.ForEach((guid: string) => {
			this.excludedGroupGuids.push(guid);
		});
	}

	public addUserToSelectedGroups(selectedGroups: GroupDto[]): void {
		if (!!selectedGroups && selectedGroups.length > 0) {
			selectedGroups.forEach((group: GroupDto) => {
				this.userGroups.Add(group);
			});
			this.rebuildEntries();
			this.form.markAsDirty();
		}
	}

	public removeGroup(group: GroupDto): void {
		this.userGroups.Remove(group);
		this.rebuildEntries();
		this.form.markAsDirty();
	}

	public get addedUserGroups(): HashSet<string> {
		let userGroupsGuids = this.groupsToGuids(this.userGroups);
		let originalUserGroupsGuids = this.groupsToGuids(this.originalUserGroups);
		return ObjUtils.SetDifference(userGroupsGuids, originalUserGroupsGuids);
	}

	public get removedUserGroups(): HashSet<string> {
		let userGroupsGuids = this.groupsToGuids(this.userGroups);
		let originalUserGroupsGuids = this.groupsToGuids(this.originalUserGroups);
		return ObjUtils.SetDifference(originalUserGroupsGuids, userGroupsGuids);
	}

	private groupsToGuids(groups: HashSet<GroupDto>): HashSet<string> {
		let result = new HashSet<string>();
		groups.ForEach((group: GroupDto) => result.Add(group.Guid));
		return result;
	}

}
